import React, { useState } from 'react';
import { byDate } from './api';
import { format, addMonths, parseISO } from 'date-fns';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Loading from '../molcules/Loading/Loading';
import { Link } from 'react-router-dom';
import { useContext } from 'react';
import { store } from '../store';
import Button from '@mui/material/Button';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import { useQuery } from '@tanstack/react-query';
import DateRangeNew from '../molcules/DateRange/DateRangeNew';

const Container = styled('div')({
  marginBottom: 24
});

const DateRangeContainer = styled('div')({
  display: 'flex',
  gap: 16,
  marginBottom: 24,
  alignItems: 'center'
});

const DayRow = styled('div')({
  display: 'flex',
  gap: 16,
  marginTop: 24,
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: 8
  }
});

const DayHeader = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  minWidth: 120,
  paddingTop: 4,
  '@media (max-width: 768px)': {
    fontSize: 16,
    paddingBottom: 8,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    minWidth: 'auto'
  }
}));

const EventsList = styled('div')({
  flex: 1
});

const EventCard = styled(Paper)(({ theme }) => ({
  padding: 2,
  marginBottom: 12,
  cursor: 'pointer',
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[8]
  }
}));

const EventHeader = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 12,
  flexWrap: 'wrap',
  gap: 12
});

const EventTitle = styled('div')({
  fontSize: 14,
  fontWeight: 'bold',
  marginBottom: 6
});

const EventTime = styled('div')(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.primary,
  marginBottom: 4
}));

const EventDetails = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 12,
  marginTop: 12
});

const EventDetailItem = styled('div')({
  display: 'flex',
  flexDirection: 'column'
});

const DetailLabel = styled('span')(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  marginBottom: 4
}));

const DetailValue = styled('span')({
  fontSize: 14,
  fontWeight: 500
});

const NoEvents = styled('div')({
  textAlign: 'center',
  padding: 48,
  fontSize: 16,
  color: '#666'
});

export default function EventsSchedule() {
  const { state } = useContext(store);
  const [dateRange, setDateRange] = useState([new Date(), addMonths(new Date(), 3)]);

  const { data, isLoading } = useQuery({
    queryKey: ['eventsSchedule', state.site?._id, dateRange[0], dateRange[1]],
    queryFn: async () => {
      const res = await byDate({
        from: format(dateRange[0], 'yyyy-MM-dd'),
        to: format(dateRange[1], 'yyyy-MM-dd'),
        site: state.site._id
      });

      if (!res.data) {
        return { events: [], groupedEvents: {} };
      }

      // Sort events by start date and time
      const sortedEvents = res.data.sort((a, b) => {
        return new Date(a.startDate) - new Date(b.startDate);
      });

      // Group events by day
      const grouped = {};
      sortedEvents.forEach(event => {
        const dayKey = format(parseISO(event.startDate), 'yyyy-MM-dd');
        if (!grouped[dayKey]) {
          grouped[dayKey] = [];
        }
        grouped[dayKey].push(event);
      });

      return { events: sortedEvents, groupedEvents: grouped };
    },
    enabled: !!dateRange[0] && !!dateRange[1] && !!state.site?._id,
    refetchOnWindowFocus: false
  });

  const events = data?.events || [];
  const groupedEvents = data?.groupedEvents || {};

  const renderEvent = (event) => {
    const linkTo = state.site?.normalName
      ? `/${state.site.normalName}/events/view/${event._id}`
      : `/events/view/${event._id}`;

    return (
      <Link to={linkTo} key={event._id} style={{ textDecoration: 'none', color: 'inherit' }}>
        <EventCard>
          <EventHeader>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <EventTime>
                {format(parseISO(event.startDate), 'HH:mm')} - {format(parseISO(event.endDate), 'HH:mm')}
              </EventTime>
              <EventTitle>{event.groupName}</EventTitle>
            </div>
            <div style={{ textAlign: 'right' }}>
              {event.area && (
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                  {event.area}
                </div>
              )}
              {event.type && (
                <div style={{ fontSize: 13, color: '#666' }}>
                  {event.type}
                </div>
              )}
            </div>
          </EventHeader>

          <EventDetails>
            {event.customerName && (
              <EventDetailItem>
                <DetailLabel>Customer</DetailLabel>
                <DetailValue>{event.customerName}</DetailValue>
              </EventDetailItem>
            )}
            {event.company && (
              <EventDetailItem>
                <DetailLabel>Company</DetailLabel>
                <DetailValue>{event.company}</DetailValue>
              </EventDetailItem>
            )}
            {(event.sleepers || event.sleepers === 0) && (
              <EventDetailItem>
                <DetailLabel>Sleepers</DetailLabel>
                <DetailValue>{event.sleepers}</DetailValue>
              </EventDetailItem>
            )}
            {(event.dayGuests || event.dayGuests === 0) && (
              <EventDetailItem>
                <DetailLabel>Day Guests</DetailLabel>
                <DetailValue>{event.dayGuests}</DetailValue>
              </EventDetailItem>
            )}
            {event.totalPrice && (
              <EventDetailItem>
                <DetailLabel>Total Price</DetailLabel>
                <DetailValue>£{event.totalPrice.toLocaleString()}</DetailValue>
              </EventDetailItem>
            )}
            {event.contactName && (
              <EventDetailItem>
                <DetailLabel>Contact</DetailLabel>
                <DetailValue>{event.contactName}</DetailValue>
              </EventDetailItem>
            )}
            {event.contactPhone && (
              <EventDetailItem>
                <DetailLabel>Phone</DetailLabel>
                <DetailValue>{event.contactPhone}</DetailValue>
              </EventDetailItem>
            )}
          </EventDetails>

          {event.notes && (
            <div style={{ marginTop: 12, fontSize: 13, color: '#666', fontStyle: 'italic' }}>
              {event.notes}
            </div>
          )}
        </EventCard>
      </Link>
    );
  };

  return (
    <Container>
      <div className="heading" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <span className="heading-text">Events Schedule</span>
        <Button
          component={Link}
          to={`/${state.site.normalName}/events`}
          variant="contained"
          color="primary"
          startIcon={<ViewWeekIcon />}
        >
          Timeline View
        </Button>
      </div>

      <DateRangeContainer>
        <DateRangeNew
          value={dateRange}
          onChange={setDateRange}
          altStyle={true}
          title="Date Range"
        />
      </DateRangeContainer>

      {isLoading && <Loading />}

      {!isLoading && events.length === 0 && (
        <NoEvents>
          No events found in the selected date range.
        </NoEvents>
      )}

      {!isLoading && Object.keys(groupedEvents).map(dayKey => (
        <DayRow key={dayKey}>
          <DayHeader>
            {format(parseISO(dayKey), 'd MMM, EEE')}
          </DayHeader>
          <EventsList>
            {groupedEvents[dayKey].map(event => renderEvent(event))}
          </EventsList>
        </DayRow>
      ))}
    </Container>
  );
}
